import React from 'react';
import { getChannelDisplayName } from '@/hooks/useChannel';
import { Channel } from '@/types';
import { cn } from '@/utils';

interface HeaderProps {
  onChannelChange: (channel: Channel) => void;
  selectedChannel: Channel;
}

export const Header: React.FC<HeaderProps> = ({ onChannelChange, selectedChannel }) => {
  const currentTime = new Date().toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  const systemStatus = {
    uptime: 99.8,
    status: 'healthy' as const,
    issues: [] as string[],
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy':
        return 'text-emerald-400';
      case 'degraded':
        return 'text-amber-400';
      case 'down':
        return 'text-red-400';
      default:
        return 'text-slate-400';
    }
  };

  return (
    <header className="bg-slate-900 border-b border-slate-600 h-20 flex items-center justify-between px-6">
      {/* Left: Logo and timestamp */}
      <div className="flex items-center space-x-6">
        <h1 className="text-xl font-bold text-slate-50">
          AFN Nurture Agent
        </h1>
        <div className="text-sm text-slate-400 font-mono">
          {currentTime}
        </div>
      </div>

      {/* Center: System status */}
      <div className="flex items-center space-x-4">
        <div className="flex items-center space-x-2">
          <div className={cn('w-2 h-2 rounded-full', 
            systemStatus.status === 'healthy' ? 'bg-emerald-500' : 
            systemStatus.status === 'degraded' ? 'bg-amber-500' : 'bg-red-500'
          )} />
          <span className={cn('text-sm', getStatusColor(systemStatus.status))}>
            System {systemStatus.uptime}%
          </span>
        </div>
        <div className="text-xs text-slate-500">
          All services operational
        </div>
      </div>

      {/* Right: Channel toggle */}
      <div className="flex items-center space-x-2">
        <span className="text-sm text-slate-400 mr-3">Channel:</span>
        <div className="flex bg-slate-800 rounded-lg p-1">
          {(['RETAIL', 'SL'] as Channel[]).map((channel) => (
            <button
              key={channel}
              onClick={() => onChannelChange(channel)}
              className={cn(
                'px-4 py-2 text-sm font-medium rounded-md transition-colors',
                selectedChannel === channel
                  ? 'bg-blue-600 text-white'
                  : 'text-slate-300 hover:text-white hover:bg-slate-700'
              )}
            >
              {getChannelDisplayName(channel)}
            </button>
          ))}
        </div>
      </div>
    </header>
  );
};